import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useFormat } from '../../../i18n/format.js';
import {
  X,
  Shield,
  FileText,
  Calendar,
  Euro,
  CheckCircle,
  Clock,
  AlertTriangle,
  Building2,
  Car,
  Upload,
  Plus,
  ExternalLink,
  ChevronDown,
  Minus,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  Pencil,
  Check,
} from 'lucide-react';
import api, { insuranceModuleAPI } from '../../../api.js';
import { API_BASE_URL, getAuthHeaders } from '../../../api/core.js';
import { documentAPI } from '../../../api.js';
import { CLAIM_STATUS_LABELS, CLOSED_CLAIM_STATUSES } from '../../../hooks/useVehicleInsuranceData.js';
import VehicleAddTool from './VehicleAddTool.jsx';
import { isSingleVehicleContract } from '../../../utils/insuranceStatus.js';
import VehicleRemoveTool from './VehicleRemoveTool.jsx';
import InsuranceVerificationModal from './InsuranceVerificationModal.jsx';
import DocumentViewerModal from '../../common/DocumentViewerModal.jsx';
import DocumentValidationModal from '../../documents/DocumentValidationModal.jsx';
import UnifiedDocumentsTab from '../../common/UnifiedDocumentsTab.jsx';

// Tab configuration — libelles resolus par t('insurance.contractModal.tabs.<id>')
const TABS = [
  { id: 'summary', icon: FileText },
  { id: 'vehicles', icon: Car, showCount: true },
  { id: 'guarantees', icon: Shield },
  { id: 'claims', icon: AlertTriangle, showCount: true },
  { id: 'documents', icon: FileText },
];

// Types de contrat        -> t('insurance.contractModal.contractType.<code>')
// Frequences de paiement  -> t('insurance.contractModal.paymentFrequency.<code>')
// Statuts                 -> t('insurance.contractStatus.<code>')

function ContractDetailModal({ contract, onClose, onUpdate, onMessage, onWorkflowAction }) {
  const { t, fmt: uiFmt } = useFormat();
  // Devise de la filiale du contrat - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const [confirmingAction, setConfirmingAction] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [vehicleInsurances, setVehicleInsurances] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddTool, setShowAddTool] = useState(false);
  const [showRemoveTool, setShowRemoveTool] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [contractDocs, setContractDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [validationDoc, setValidationDoc] = useState(null);
  const [intermediaryName, setIntermediaryName] = useState(null);
  const [intermediaryId, setIntermediaryId] = useState(null);
  const [allBrokers, setAllBrokers] = useState([]);
  const [editingBroker, setEditingBroker] = useState(false);
  const [orgName, setOrgName] = useState(null);
  const [coverageBlocks, setCoverageBlocks] = useState([]);
  const [coverageModels, setCoverageModels] = useState([]); // All available models with guarantees
  const [expandedGroups, setExpandedGroups] = useState({}); // accordion state for guarantees tab
  const [showAllVehicles, setShowAllVehicles] = useState({}); // { modelId: true } to show all vehicles in group
  const [verificationStatuses, setVerificationStatuses] = useState({});
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verifyingVi, setVerifyingVi] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { viId, field }
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const needsParentRefresh = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowActionsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load related data
  useEffect(() => {
    if (!contract) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [viRes, vRes, clRes] = await Promise.all([
          api.vehicleInsurance.getAll({ contract_id: contract.id }),
          api.vehicle.getAll(),
          api.claim.getAll({ contract_id: contract.id }),
        ]);

        // Filter out removed/terminated records
        const activeVi = (viRes.data || []).filter(vi => !vi.removed_at);
        if (viRes.ok) setVehicleInsurances(activeVi);
        // Only keep vehicles that are actively linked to this contract
        if (viRes.ok && vRes.ok) {
          const linkedVehicleIds = new Set(activeVi.map(vi => vi.vehicle_id));
          setVehicles((vRes.data || []).filter(v => linkedVehicleIds.has(v.id)));
        } else if (vRes.ok) {
          setVehicles(vRes.data || []);
        }
        if (clRes.ok) setClaims(clRes.data.filter(c => c.contract_id === contract.id) || []);

        // Load coverage blocks for this contract via insurance module service
        try {
          const cbRes = await insuranceModuleAPI.getContractCoverage(contract.id);
          if (cbRes.ok) {
            setCoverageBlocks(cbRes.data || []);
          }
        } catch (e) { /* ignore */ }

        // Load coverage models catalog (for per-vehicle coverage selector)
        try {
          const cmRes = await insuranceModuleAPI.getModels();
          if (cmRes.ok) {
            setCoverageModels(cmRes.data || []);
          }
        } catch (e) { /* ignore */ }

        // Load verification statuses for vehicles on this contract
        try {
          const viList = viRes.ok ? (viRes.data || []) : [];
          const verMap = {};
          for (const vi of viList) {
            try {
              const vsRes = await insuranceModuleAPI.getVerificationStatus(vi.vehicle_id);
              if (vsRes.ok && vsRes.data) {
                verMap[vi.id] = vsRes.data;
              }
            } catch { /* ignore */ }
          }
          setVerificationStatuses(verMap);
        } catch { /* ignore */ }

        // Load all brokers for the selector + resolve current intermediary name
        try {
          const suppRes = await api.supplier.getAll();
          if (suppRes.ok) {
            const brokers = (suppRes.data || []).filter(s => ['broker', 'insurer', 'agent'].includes(s.category));
            setAllBrokers(brokers);
          }
        } catch { /* ignore */ }

        setIntermediaryId(contract.intermediary_id || null);
        if (contract.intermediary_id) {
          try {
            const intRes = await api.supplier.getById(contract.intermediary_id);
            if (intRes.ok && intRes.data) {
              setIntermediaryName(intRes.data.name || intRes.data.company_name || null);
            }
          } catch (e) { /* ignore */ }
        }

        // Resolve organization name
        if (contract.org_id) {
          try {
            const orgRes = await fetch(`${API_BASE_URL}/organization?code=${encodeURIComponent(contract.org_id)}&_limit=1`, {
              headers: getAuthHeaders()
            });
            const orgData = await orgRes.json();
            const orgs = orgData?.data || [];
            if (orgs.length > 0) setOrgName(orgs[0].name);
          } catch (e) { /* ignore */ }
        }
      } catch (err) {
        console.error('Error loading contract details:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // Depend on contract.id, NOT the whole contract object. Otherwise every
    // parent re-render passes a new reference for the same contract, the
    // effect re-fires, and the fresh fetch clobbers inline edits (premium,
    // coverage) the user just made — looks like "my change didn't save"
    // when in fact it did, we just stomped it with a stale GET.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id]);

  // Contract document reference ID (CNT_000001 format)
  const contractRefId = useMemo(() => {
    if (!contract?.id) return null;
    return `CNT_${String(contract.id).padStart(6, '0')}`;
  }, [contract?.id]);

  // Load contract documents
  const loadContractDocs = useCallback(async () => {
    if (!contractRefId) return;
    setDocsLoading(true);
    try {
      const res = await documentAPI.getAll({ scope: 'CONTRACT', reference_id: contractRefId });
      if (res.ok) setContractDocs(res.data || []);
    } catch (err) {
      console.warn('Failed to load contract documents:', err);
    } finally {
      setDocsLoading(false);
    }
  }, [contractRefId]);

  useEffect(() => {
    if (contractRefId) loadContractDocs();
  }, [contractRefId, loadContractDocs]);

  // Handle document click - route to viewer or validation modal
  const handleDocumentOpen = (doc) => {
    if (doc.validation_status === 'pending' || doc.validation_status === 'rejected') {
      setValidationDoc(doc);
    } else {
      setViewerDoc(doc);
    }
  };

  // Argent : un seul outil (utils/formatMoney via useOrgCurrency), devise de la
  // filiale du contrat. Toujours 2 decimales ; coerce les `numeric` PG en string.
  const fmtEur = (v) => (Number.isFinite(Number(v)) ? money(v, contract) : '—');

  // Calculate vehicle counts and total premium (exclude orphaned insurance records)
  // IMPORTANT: coerce premium_amount to Number. PostgreSQL's `numeric` column
  // type serializes to STRING via the `pg` driver ("598.00"), not a JS number.
  // `sum + "598.00"` is string concatenation and yields garbage like
  // "0598.00549.00199.00" for 3 vehicles. Number() first → real addition.
  const { vehicleCount, totalPremium } = useMemo(() => {
    const existingVehicleIds = new Set(vehicles.map(v => v.id));
    const validVIs = vehicleInsurances.filter(vi => existingVehicleIds.has(vi.vehicle_id));
    const count = validVIs.length;
    const total = validVIs.reduce((sum, vi) => sum + (Number(vi.premium_amount) || 0), 0);
    return { vehicleCount: count, totalPremium: total };
  }, [vehicleInsurances, vehicles]);

  // Get vehicle details by ID
  const getVehicle = vehicleId => vehicles.find(v => v.id === vehicleId);

  // Inline edit: start editing a cell
  const startEdit = (viId, field, currentValue) => {
    setEditingCell({ viId, field });
    setEditValue(currentValue || '');
    setTimeout(() => editInputRef.current?.select(), 50);
  };

  // Inline edit: save value
  const saveEdit = async () => {
    if (!editingCell) return;
    const { viId, field } = editingCell;
    const numVal = editValue === '' ? null : parseFloat(editValue);
    const vi = vehicleInsurances.find(v => v.id === viId);
    if (!vi || (numVal !== null && isNaN(numVal))) {
      setEditingCell(null);
      return;
    }
    // Skip save if unchanged
    if (numVal === (vi[field] || null)) {
      setEditingCell(null);
      return;
    }
    try {
      // Use insurance module API (syncs contract total automatically for premiums)
      if (field === 'premium_amount') {
        await insuranceModuleAPI.setVehiclePremium(viId, numVal);
      } else if (field === 'deductible_amount') {
        await insuranceModuleAPI.setVehicleDeductible(viId, numVal);
      }
      // Update local state immediately
      const updated = vehicleInsurances.map(v =>
        v.id === viId ? { ...v, [field]: numVal } : v
      );
      setVehicleInsurances(updated);
      // Mark for parent refresh on close (don't refresh now — it disrupts the modal)
      if (field === 'premium_amount') needsParentRefresh.current = true;
      onMessage?.(t('insurance.contractModal.amountUpdated'), 'success');
    } catch {
      onMessage?.(t('insurance.contractModal.updateFailed'), 'error');
    }
    setEditingCell(null);
  };

  const cancelEdit = () => setEditingCell(null);

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
    if (e.key === 'Tab') {
      e.preventDefault();
      saveEdit();
    }
  };

  // Update coverage model for a vehicle insurance record
  const updateVehicleCoverage = async (viId, modelId) => {
    try {
      const model = coverageModels.find(m => m.id === parseInt(modelId));
      await fetch(`${API_BASE_URL}/vehicle_insurance/${viId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Canon only (coverage_model_id FK). Writing the deprecated legacy
          // string coverage column here is what kept reviving the insurance
          // dual-schema (modale vs onglet contrat désynchronisés). Dropped.
          coverage_model_id: modelId ? parseInt(modelId) : null,
        }),
      });
      // Update local state
      setVehicleInsurances(prev => prev.map(vi =>
        vi.id === viId ? { ...vi, coverage_model_id: modelId ? parseInt(modelId) : null } : vi
      ));
      onMessage?.(t('insurance.contractModal.coverageUpdated', {
        coverage: model?.name || t('insurance.contractModal.noCoverage'),
      }), 'success');
    } catch {
      onMessage?.(t('insurance.contractModal.coverageUpdateFailed'), 'error');
    }
  };

  // Count active claims
  const activeClaimsCount = useMemo(() => {
    return claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).length;
  }, [claims]);

  // Workflow state configuration
  // Les libelles d'etat viennent de t('insurance.contractStatus.<key>') au rendu.
  const workflowStates = [
    { key: 'draft', icon: FileText, color: 'gray' },
    { key: 'active', icon: CheckCircle, color: 'green' },
  ];

  const terminalStates = [
    { key: 'suspended', icon: AlertTriangle, color: 'orange' },
    { key: 'terminated', icon: X, color: 'red' },
    { key: 'expired', icon: Clock, color: 'gray' },
  ];

  // Get workflow actions based on current state
  const getWorkflowActions = status => {
    const actions = {
      draft: [],
      active: [
        { key: 'suspend', color: 'orange', icon: AlertTriangle },
        { key: 'terminate', color: 'red', icon: X },
      ],
      suspended: [{ key: 'reactivate', color: 'green', icon: CheckCircle }],
    };
    return actions[status] || [];
  };

  const handleAction = async action => {
    setConfirmingAction(action);
  };

  const confirmAction = async () => {
    if (!confirmingAction) return;

    try {
      // Use workflow API directly or call parent handler
      if (onWorkflowAction) {
        await onWorkflowAction(contract.id, confirmingAction.key);
      } else {
        // Fallback: use workflow API directly
        const workflowActions = {
          suspend: api.insurance?.workflow?.suspend,
          reactivate: api.insurance?.workflow?.reactivate,
          terminate: api.insurance?.workflow?.terminate,
        };
        const workflowMethod = workflowActions[confirmingAction.key];
        if (workflowMethod) {
          await workflowMethod(contract.id);
        }
      }
      setConfirmingAction(null);
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Workflow action failed:', error);
    }
  };

  // Get color classes for state
  const getStateColor = stateKey => {
    const state = [...workflowStates, ...terminalStates].find(s => s.key === stateKey);
    const colors = {
      gray: 'bg-gray-100 text-gray-800 border-gray-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      blue: 'bg-blue-100 text-blue-800 border-blue-300',
      purple: 'bg-purple-100 text-purple-800 border-purple-300',
      green: 'bg-green-100 text-green-800 border-green-300',
      orange: 'bg-orange-100 text-orange-800 border-orange-300',
      red: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[state?.color] || colors.gray;
  };

  const availableActions = getWorkflowActions(contract.status);

  // Calculate days until expiry
  const getDaysUntilExpiry = () => {
    if (!contract.expiry_date) return null;
    const today = new Date();
    const expiry = new Date(contract.expiry_date);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntil = getDaysUntilExpiry();
  // Tacit renewal: an active contract past its expiry_date is NOT expired — it auto-renewed
  const isTacitRenewal = contract.status === 'active' && daysUntil !== null && daysUntil < 0;
  const isExpired = daysUntil !== null && daysUntil < 0 && !isTacitRenewal;
  const isExpiringSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 60;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[950px] h-[700px] overflow-hidden flex flex-col">
        {/* Header with Actions Dropdown */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">
                {contract.product_name || t('insurance.contractModal.defaultTitle')}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-blue-100 text-sm">{t('insurance.contractModal.policyNumber', { number: contract.policy_number })}</span>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    contract.status === 'active'
                      ? 'bg-green-400 text-green-900'
                      : contract.status === 'suspended'
                        ? 'bg-orange-400 text-orange-900'
                        : contract.status === 'terminated'
                          ? 'bg-red-400 text-red-900'
                          : 'bg-blue-300 text-blue-900'
                  }`}
                >
                  {['active', 'draft', 'suspended', 'terminated'].includes(contract.status)
                    ? t(`insurance.contractStatus.${contract.status}`)
                    : contract.status || t('common.states.notAvailable')}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Workflow Actions Dropdown */}
              {availableActions.length > 0 && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                  >
                    {t('insurance.contractModal.actions')}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${showActionsDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {showActionsDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                      {availableActions.map(action => {
                        const Icon = action.icon;
                        return (
                          <button
                            key={action.key}
                            onClick={() => {
                              setShowActionsDropdown(false);
                              handleAction(action);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 ${
                              action.color === 'red'
                                ? 'text-red-600'
                                : action.color === 'orange'
                                  ? 'text-orange-600'
                                  : action.color === 'green'
                                    ? 'text-green-600'
                                    : action.color === 'blue'
                                      ? 'text-blue-600'
                                      : action.color === 'purple'
                                        ? 'text-purple-600'
                                        : 'text-gray-700'
                            }`}
                          >
                            <Icon size={16} />
                            {t(`insurance.workflowActions.${action.key}`, { defaultValue: action.key })}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <button onClick={() => { if (needsParentRefresh.current) onUpdate?.(); onClose(); }} className="text-white hover:text-gray-200 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6 bg-gray-50">
          <div className="flex space-x-1 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const count =
                tab.id === 'vehicles'
                  ? vehicleCount
                  : tab.id === 'claims'
                    ? activeClaimsCount
                    : null;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-blue-600 bg-white'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(`insurance.contractModal.tabs.${tab.id}`)}
                  {tab.showCount && count !== null && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Summary Tab */}
          {!loading && activeTab === 'summary' && (
            <>
              {/* Contract Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* General Information */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    {t('insurance.contractModal.generalInfo')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.policyNumberLabel')}</span>
                      <span className="font-medium">{contract.policy_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.insurer')}</span>
                      <span className="font-medium">{contract.insurer_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.contractTypeLabel')}</span>
                      <span className="font-medium">{contract.contract_type ? t(`insurance.contractModal.contractType.${contract.contract_type}`, { defaultValue: contract.contract_type }) : t('common.states.notAvailable')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.organization')}</span>
                      <span className="font-medium">{orgName || contract.org_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.statusLabel')}</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStateColor(contract.status)}`}
                      >
                        {t(`insurance.contractStatus.${contract.status}`, { defaultValue: contract.status })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Coverage Period */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    {t('insurance.contractModal.coveragePeriod')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.effectiveDate')}</span>
                      <span className="font-medium">
                        {contract.effective_date
                          ? uiFmt.date(contract.effective_date)
                          : t('common.states.notAvailable')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.expiryDate')}</span>
                      <span
                        className={`font-bold ${
                          isExpired
                            ? 'text-red-600'
                            : isTacitRenewal
                              ? 'text-blue-600'
                              : isExpiringSoon
                                ? 'text-yellow-600'
                                : 'text-green-600'
                        }`}
                      >
                        {contract.expiry_date
                          ? uiFmt.date(contract.expiry_date)
                          : t('common.states.notAvailable')}
                      </span>
                    </div>
                    {daysUntil !== null && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        {isTacitRenewal ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-blue-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-bold">
                                {t('insurance.contractModal.tacitRenewal')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 ml-6">
                              {t('insurance.contractModal.tacitRenewalDetail', { n: Math.abs(daysUntil) })}
                            </p>
                          </div>
                        ) : isExpired ? (
                          <div className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs font-bold">
                              {t('insurance.contractModal.expiredSince', { n: Math.abs(daysUntil) })}
                            </span>
                          </div>
                        ) : isExpiringSoon ? (
                          <div className="flex items-center gap-2 text-yellow-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs font-bold">
                              {t('insurance.contractModal.expiresIn', { n: daysUntil })}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">{t('insurance.contractModal.valid', { n: daysUntil })}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Details */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Euro className="w-5 h-5 text-amber-600" />
                    {t('insurance.contractModal.financialDetails')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.annualPremium')}</span>
                      <span className="text-lg font-bold text-gray-900">
                        {fmtEur(totalPremium || contract.total_premium_ttc || 0)}
                      </span>
                    </div>
                    {contract.contract_type !== 'motor_fleet' && (() => {
                      // On a mono contract the "contract franchise" is redundant
                      // (1 vehicle = 1 franchise). Derive from the single active VI
                      // so the summary stays in sync with the Véhicules tab.
                      const activeVi = (vehicleInsurances || []).find(vi => !vi.removed_at);
                      const derivedDeductible = activeVi?.deductible_amount ?? contract.deductible ?? 0;
                      return (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('insurance.contractModal.deductible')}</span>
                          <span className="font-medium">{money(derivedDeductible, contract)}</span>
                        </div>
                      );
                    })()}
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.paymentFrequencyLabel')}</span>
                      <span className="font-medium">{contract.payment_frequency ? t(`insurance.contractModal.paymentFrequency.${contract.payment_frequency}`, { defaultValue: contract.payment_frequency }) : t('common.states.notAvailable')}</span>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    {t('insurance.contractModal.additionalInfo')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('insurance.contractModal.intermediary')}</span>
                      {editingBroker ? (
                        <div className="inline-flex items-center gap-1">
                          <select
                            value={intermediaryId || ''}
                            onChange={(e) => setIntermediaryId(e.target.value ? parseInt(e.target.value) : null)}
                            className="text-xs border border-blue-300 rounded px-2 py-1 bg-blue-50 focus:ring-1 focus:ring-blue-500 outline-none"
                          >
                            <option value="">{t('insurance.contractModal.noneOption')}</option>
                            {allBrokers.map(b => (
                              <option key={b.id} value={b.id}>{b.name} ({t(`insurance.contractModal.brokerCategory.${b.category}`, { defaultValue: b.category })})</option>
                            ))}
                          </select>
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${API_BASE_URL}/insurance_contract/${contract.id}`, {
                                  method: 'PUT',
                                  headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ intermediary_id: intermediaryId }),
                                });
                                const broker = allBrokers.find(b => b.id === intermediaryId);
                                setIntermediaryName(broker?.name || null);
                                setEditingBroker(false);
                                needsParentRefresh.current = true;
                                onMessage?.(t('insurance.contractModal.intermediaryUpdated'), 'success');
                              } catch {
                                onMessage?.(t('insurance.contractModal.updateFailed'), 'error');
                              }
                            }}
                            className="px-1.5 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            title={t('insurance.contractModal.validate')}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => { setIntermediaryId(contract.intermediary_id || null); setEditingBroker(false); }}
                            className="px-1.5 py-1 text-gray-400 hover:text-gray-600" title={t('common.actions.cancel')}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingBroker(true)}
                          className="group inline-flex items-center gap-1 font-medium hover:text-blue-600 transition-colors"
                          title={t('insurance.contractModal.clickToEdit')}
                        >
                          <span>{intermediaryName || t('insurance.contractModal.directNone')}</span>
                          <Pencil className="w-3 h-3 text-gray-300 group-hover:text-blue-500" />
                        </button>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('insurance.contractModal.createdAt')}</span>
                      <span className="font-medium">
                        {(contract.created_at || contract.effective_date)
                          ? uiFmt.date(contract.created_at || contract.effective_date)
                          : t('common.states.notAvailable')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {contract.notes && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">{t('insurance.contractModal.notes')}</h4>
                  <p className="text-sm text-blue-800">{contract.notes}</p>
                </div>
              )}
            </>
          )}

          {/* Vehicles Tab */}
          {!loading && activeTab === 'vehicles' && (
            <div className="space-y-4">
              {/* Header with Add/Remove buttons */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('insurance.contractModal.insuredVehicles')}</h3>
                  <p className="text-sm text-gray-500">
                    {t('insurance.contractModal.vehiclesAndPremium', { n: vehicleCount, amount: fmtEur(totalPremium) })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const isMono = isSingleVehicleContract(contract);
                    const removeDisabled = vehicleInsurances.length === 0 || isMono;
                    const removeTooltip = isMono
                      ? t('insurance.contractModal.removeMonoTooltip')
                      : '';
                    return (
                      <button
                        onClick={() => !removeDisabled && setShowRemoveTool(true)}
                        disabled={removeDisabled}
                        title={removeTooltip}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          removeDisabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                        }`}
                        data-tour="contract-remove-vehicle"
                      >
                        <Minus className="w-4 h-4" />
                        {t('insurance.vehicleTab.remove')}
                      </button>
                    );
                  })()}
                  {(() => {
                    const monoSlotTaken = isSingleVehicleContract(contract) && vehicleInsurances.length >= 1;
                    return (
                      <button
                        onClick={() => !monoSlotTaken && setShowAddTool(true)}
                        disabled={monoSlotTaken}
                        title={monoSlotTaken ? t('insurance.contractModal.addMonoTooltip') : ''}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          monoSlotTaken
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        data-tour="contract-add-vehicle"
                      >
                        <Plus className="w-4 h-4" />
                        {t('common.actions.add')}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* Vehicle list */}
              {vehicleInsurances.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {t('insurance.contractModal.col.vehicle')}
                        </th>
                        <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {t('insurance.contractModal.col.coverage')}
                        </th>
                        <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {t('insurance.contractModal.col.premium')}
                        </th>
                        <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {t('insurance.contractModal.col.deductible')}
                        </th>
                        <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {t('insurance.contractModal.col.verification')}
                        </th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {t('insurance.contractModal.col.effectiveDate')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {vehicleInsurances.filter(vi => vehicles.some(v => v.id === vi.vehicle_id)).map(vi => {
                        const vehicle = getVehicle(vi.vehicle_id);
                        const vs = verificationStatuses[vi.id];
                        return (
                          <tr key={vi.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <Car className="w-4 h-4 text-gray-400" />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {vehicle?.license_plate || t('common.states.notAvailable')}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {vehicle?.make} {vehicle?.model}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {/* Direct save on select change — no pending/validate ceremony.
                                  The previous two-step (select → validate ✓) was unclear;
                                  users didn't realize they had to press the check button
                                  and the selection silently reverted. */}
                              <select
                                value={vi.coverage_model_id != null ? String(vi.coverage_model_id) : ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateVehicleCoverage(vi.id, val || null);
                                }}
                                className={`text-xs border rounded px-2 py-1 outline-none cursor-pointer ${
                                  vi.coverage_model_id
                                    ? 'border-green-300 bg-green-50 text-green-800 font-medium focus:ring-1 focus:ring-green-500'
                                    : 'border-gray-300 bg-white hover:border-blue-400 focus:ring-1 focus:ring-blue-500'
                                }`}
                              >
                                <option value="">{t('insurance.contractModal.chooseOption')}</option>
                                {coverageModels.map(m => (
                                  <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {editingCell?.viId === vi.id && editingCell?.field === 'premium_amount' ? (
                                <input
                                  ref={editInputRef}
                                  type="number"
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  onBlur={saveEdit}
                                  onKeyDown={handleEditKeyDown}
                                  autoFocus
                                  className="w-24 px-2 py-1 text-xs text-right border border-blue-400 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                                  step="0.01"
                                  min="0"
                                />
                              ) : (
                                <button
                                  onClick={() => startEdit(vi.id, 'premium_amount', vi.premium_amount)}
                                  className="group inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-blue-50 text-gray-700 transition-colors"
                                  title={t('insurance.contractModal.clickToEdit')}
                                >
                                  <span>{vi.premium_amount != null && vi.premium_amount !== '' ? fmtEur(vi.premium_amount) : '-'}</span>
                                  <Pencil className="w-3 h-3 text-gray-300 group-hover:text-blue-500" />
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {editingCell?.viId === vi.id && editingCell?.field === 'deductible_amount' ? (
                                <input
                                  ref={editInputRef}
                                  type="number"
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  onBlur={saveEdit}
                                  onKeyDown={handleEditKeyDown}
                                  autoFocus
                                  className="w-24 px-2 py-1 text-xs text-right border border-blue-400 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                                  step="0.01"
                                  min="0"
                                />
                              ) : (
                                <button
                                  onClick={() => startEdit(vi.id, 'deductible_amount', vi.deductible_amount)}
                                  className="group inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-blue-50 text-gray-700 transition-colors"
                                  title={t('insurance.contractModal.clickToEdit')}
                                >
                                  <span>{vi.deductible_amount != null && vi.deductible_amount !== '' ? fmtEur(vi.deductible_amount) : '-'}</span>
                                  <Pencil className="w-3 h-3 text-gray-300 group-hover:text-blue-500" />
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {(() => {
                                if (!vs || vs.status === 'not_verified') {
                                  return (
                                    <button
                                      onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded"
                                      title={t('insurance.vehicleTab.notVerified')}
                                    >
                                      <ShieldQuestion className="w-3.5 h-3.5" />
                                      {t('insurance.vehicleTab.notVerified')}
                                    </button>
                                  );
                                }
                                if (vs.status === 'verified_ok') {
                                  return (
                                    <button
                                      onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-100 hover:bg-green-200 rounded"
                                      title={t('insurance.vehicleTab.verified')}
                                    >
                                      <ShieldCheck className="w-3.5 h-3.5" />
                                      {t('insurance.vehicleTab.verified')}
                                    </button>
                                  );
                                }
                                if (vs.status === 'verified_discrepancy') {
                                  return (
                                    <button
                                      onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 bg-red-100 hover:bg-red-200 rounded"
                                      title={t('insurance.vehicleTab.discrepancyTitleShort')}
                                    >
                                      <ShieldAlert className="w-3.5 h-3.5" />
                                      {t('insurance.vehicleTab.discrepancies', { count: vs.discrepancy_count })}
                                    </button>
                                  );
                                }
                                return (
                                  <button
                                    onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded"
                                    title={t('insurance.vehicleTab.expiredVerification')}
                                  >
                                    <ShieldQuestion className="w-3.5 h-3.5" />
                                    {t('insurance.vehicleTab.expiredVerification')}
                                  </button>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-600">
                              {vi.assigned_at
                                ? uiFmt.date(vi.assigned_at)
                                : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-gray-700 text-xs" colSpan={2}>
                          {t('insurance.contractModal.totalVehicles', { count: vehicleCount })}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900 text-xs">
                          {totalPremium ? fmtEur(totalPremium) : '-'}
                        </td>
                        <td colSpan={4}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Car className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">{t('insurance.contractModal.noVehicleTitle')}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('insurance.contractModal.noVehicleHint')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Guarantees Tab */}
          {!loading && activeTab === 'guarantees' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t('insurance.contractModal.guaranteesTitle')}</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t('insurance.contractModal.guaranteesSubtitle', { count: vehicleCount })}
                </p>
              </div>

              {(() => {
                const fmt = (v) => (v != null ? money(v, contract) : null);
                const fmtShort = (v) => (v == null ? null : money(v, contract, { compact: true, decimals: 0 }));
                const activeVis = vehicleInsurances.filter(vi => !vi.removed_at && vehicles.some(v => v.id === vi.vehicle_id));
                const withoutModel = activeVis.filter(vi => !vi.coverage_model_id);

                // Vehicle category helper
                const getCatLabel = (v) => {
                  const cat = (v?.vehicle_category || '').toUpperCase();
                  if (cat === 'PL' || cat === 'POIDS LOURD') return 'PL';
                  if (cat === 'VU' || cat === 'UTILITAIRE') return 'VU';
                  return 'VP';
                };
                const catColors = { VP: 'bg-sky-100 text-sky-700', VU: 'bg-indigo-100 text-indigo-700', PL: 'bg-amber-100 text-amber-700' };

                // Build groups per coverage model
                const groups = coverageModels
                  .map(model => {
                    const vis = activeVis.filter(vi => vi.coverage_model_id === model.id);
                    if (vis.length === 0) return null;
                    const guarantees = model.guarantees || [];
                    const included = guarantees.filter(g => g.inclusion_type === 'included');
                    const optional = guarantees.filter(g => g.inclusion_type === 'optional');

                    // Type breakdown
                    const typeCounts = {};
                    vis.forEach(vi => {
                      const v = vehicles.find(vv => vv.id === vi.vehicle_id);
                      const cat = getCatLabel(v);
                      typeCounts[cat] = (typeCounts[cat] || 0) + 1;
                    });

                    // Avg premium
                    const premiums = vis.map(vi => vi.premium_amount || 0);
                    const avgPremium = premiums.length > 0 ? Math.round(premiums.reduce((a, b) => a + b, 0) / premiums.length) : 0;

                    // Exceptions
                    const defaultDeductible = model.default_deductible || 500;
                    const specialFranchise = vis.filter(vi => vi.deductible_amount && vi.deductible_amount > defaultDeductible);

                    return { model, vis, included, optional, typeCounts, avgPremium, specialFranchise };
                  })
                  .filter(Boolean);

                const modelColors = {
                  tous_risques: 'from-blue-50', tiers_plus: 'from-gray-50',
                  tiers_simple: 'from-slate-50', economique: 'from-orange-50',
                };

                if (activeVis.length === 0) {
                  return (
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">{t('insurance.contractModal.noVehicleTitle')}</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {/* Summary bar */}
                    <div className="grid grid-cols-4 gap-2 text-center">
                      {groups.map(g => (
                        <button key={g.model.id} onClick={() => setExpandedGroups(prev => ({ ...prev, [g.model.id]: !prev[g.model.id] }))}
                          className="bg-blue-50 border border-blue-200 rounded-lg p-2 hover:bg-blue-100 transition-colors cursor-pointer">
                          <p className="text-xl font-bold text-blue-700">{g.vis.length}</p>
                          <p className="text-[10px] text-blue-600 font-medium uppercase">{g.model.name}</p>
                        </button>
                      ))}
                      {withoutModel.length > 0 && (
                        <button onClick={() => setExpandedGroups(prev => ({ ...prev, _undefined: !prev._undefined }))}
                          className="bg-amber-50 border border-amber-200 rounded-lg p-2 hover:bg-amber-100 transition-colors cursor-pointer">
                          <p className="text-xl font-bold text-amber-600">{withoutModel.length}</p>
                          <p className="text-[10px] text-amber-600 font-medium uppercase">{t('insurance.contractModal.undefined')}</p>
                        </button>
                      )}
                    </div>

                    {/* Accordion groups */}
                    {groups.map(({ model, vis, included, optional, typeCounts, avgPremium, specialFranchise }) => {
                      const isOpen = expandedGroups[model.id];
                      const INITIAL_SHOW = 10;
                      const showAll = showAllVehicles[model.id];
                      const displayVis = showAll ? vis : vis.slice(0, INITIAL_SHOW);

                      return (
                        <div key={model.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* LEVEL 1: Management reading */}
                          <button onClick={() => setExpandedGroups(prev => ({ ...prev, [model.id]: !prev[model.id] }))}
                            className={`w-full text-left px-4 py-3 bg-gradient-to-r ${modelColors[model.level] || 'from-gray-50'} to-white hover:brightness-95 transition-all`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                                <span className="font-semibold text-gray-900">{model.name}</span>
                                <span className="text-sm text-gray-500">— {t('insurance.contractModal.vehicleCount', { count: vis.length })}</span>
                              </div>
                              {model.short_description && (
                                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium">{model.short_description}</span>
                              )}
                            </div>
                            <div className="mt-1.5 ml-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span>
                                {Object.entries(typeCounts).map(([cat, count], i) => (
                                  <span key={cat}>{i > 0 && <span className="mx-1 text-gray-300">|</span>}{count} <span className="text-gray-400">{cat}</span></span>
                                ))}
                              </span>
                              <span className="text-gray-300">·</span>
                              <span>{t('insurance.contractModal.avgPremium')} <strong className="text-gray-700">{money(avgPremium, contract)}</strong></span>
                              <span className="text-gray-300">·</span>
                              <span>{t('insurance.contractModal.guaranteesAndOptions', { guarantees: included.length, options: optional.length })}</span>
                              {specialFranchise.length > 0 && (
                                <span className="ml-auto px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium">
                                  {t('insurance.contractModal.raisedDeductibles', { count: specialFranchise.length })}
                                </span>
                              )}
                            </div>
                          </button>

                          {/* LEVEL 2: Operational reading */}
                          {isOpen && (
                            <div className="border-t border-gray-100">
                              {/* Guarantee pills */}
                              <div className="px-4 py-2.5 border-b border-gray-100">
                                <div className="flex flex-wrap gap-1">
                                  {included.map((g, idx) => (
                                    <span key={idx} className="text-[11px] bg-green-50 border border-green-200 rounded px-2 py-0.5 text-green-800">
                                      ✓ {g.name}
                                      {(g.custom_deductible || g.default_deductible) ? <span className="text-gray-400 ml-1">F:{fmtShort(g.custom_deductible || g.default_deductible)}</span> : null}
                                      {(g.custom_limit || g.default_limit) ? <span className="text-gray-400 ml-1">P:{fmtShort(g.custom_limit || g.default_limit)}</span> : null}
                                    </span>
                                  ))}
                                </div>
                                {optional.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    <span className="text-[10px] text-gray-400 mr-0.5 self-center">{t('insurance.contractModal.optionsLabel')}</span>
                                    {optional.map((g, idx) => (
                                      <span key={idx} className="text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-gray-500">{g.name}</span>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Vehicle micro-table */}
                              <div className="px-4 py-2">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-[10px] text-gray-500 uppercase border-b border-gray-200">
                                      <th className="py-1.5 text-left font-semibold pl-1">{t('insurance.contractModal.col.plate')}</th>
                                      <th className="py-1.5 text-left font-semibold">{t('insurance.contractModal.col.vehicle')}</th>
                                      <th className="py-1.5 text-center font-semibold">{t('insurance.contractModal.col.type')}</th>
                                      <th className="py-1.5 text-right font-semibold">{t('insurance.contractModal.col.premium')}</th>
                                      <th className="py-1.5 text-right font-semibold">{t('insurance.contractModal.col.deductible')}</th>
                                      <th className="py-1.5 text-left font-semibold pl-3">{t('insurance.contractModal.col.specificity')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-50 text-gray-700">
                                    {displayVis.map(vi => {
                                      const v = vehicles.find(vv => vv.id === vi.vehicle_id);
                                      const cat = getCatLabel(v);
                                      const defaultDed = model.default_deductible || 500;
                                      const isSpecialFranchise = vi.deductible_amount && vi.deductible_amount > defaultDed;
                                      return (
                                        <tr key={vi.id} className="hover:bg-blue-50/50">
                                          <td className="py-1.5 pl-1 font-mono font-medium">{v?.license_plate || '—'}</td>
                                          <td className="py-1.5 text-gray-600">{v?.make} {v?.model}</td>
                                          <td className="py-1.5 text-center">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catColors[cat]}`}>{cat}</span>
                                          </td>
                                          <td className="py-1.5 text-right">{vi.premium_amount ? fmt(vi.premium_amount) : '—'}</td>
                                          <td className={`py-1.5 text-right ${isSpecialFranchise ? 'text-red-600 font-medium' : ''}`}>
                                            {vi.deductible_amount ? fmt(vi.deductible_amount) : '—'}
                                          </td>
                                          <td className="py-1.5 pl-3">
                                            {isSpecialFranchise ? (
                                              <span className="px-1 py-0.5 bg-red-50 text-red-600 rounded text-[10px]">{t('insurance.contractModal.raisedDeductible')}</span>
                                            ) : (
                                              <span className="text-gray-400">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {vis.length > INITIAL_SHOW && (
                                  <div className="mt-2 text-center">
                                    <button
                                      onClick={() => setShowAllVehicles(prev => ({ ...prev, [model.id]: !prev[model.id] }))}
                                      className="text-xs text-blue-600 hover:text-blue-800 font-medium py-1.5 px-4 rounded-lg hover:bg-blue-50 transition-colors"
                                    >
                                      {showAll ? t('insurance.contractModal.hide') : t('insurance.contractModal.showOthers', { n: vis.length - INITIAL_SHOW })}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Undefined coverage */}
                    {withoutModel.length > 0 && (
                      <div className="border border-amber-200 rounded-lg overflow-hidden">
                        <button onClick={() => setExpandedGroups(prev => ({ ...prev, _undefined: !prev._undefined }))}
                          className="w-full text-left px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ChevronDown className={`w-4 h-4 text-amber-500 transition-transform duration-200 ${expandedGroups._undefined ? '' : '-rotate-90'}`} />
                              <span className="font-semibold text-amber-900">{t('insurance.contractModal.undefinedCoverage')}</span>
                              <span className="text-sm text-amber-600">— {t('insurance.contractModal.vehicleCount', { count: withoutModel.length })}</span>
                            </div>
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">{t('insurance.contractModal.actionRequired')}</span>
                          </div>
                          <div className="mt-1 ml-6 text-xs text-amber-700">
                            {t('insurance.contractModal.undefinedCoverageHint')}
                          </div>
                        </button>
                        {expandedGroups._undefined && (
                          <div className="px-4 py-2 border-t border-amber-100">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-[10px] text-gray-500 uppercase border-b border-gray-200">
                                  <th className="py-1.5 text-left font-semibold pl-1">{t('insurance.contractModal.col.plate')}</th>
                                  <th className="py-1.5 text-left font-semibold">{t('insurance.contractModal.col.vehicle')}</th>
                                  <th className="py-1.5 text-center font-semibold">{t('insurance.contractModal.col.type')}</th>
                                  <th className="py-1.5 text-center font-semibold">{t('insurance.contractModal.col.status')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50 text-gray-700">
                                {withoutModel.map(vi => {
                                  const v = vehicles.find(vv => vv.id === vi.vehicle_id);
                                  const cat = getCatLabel(v);
                                  return (
                                    <tr key={vi.id} className="hover:bg-amber-50/50">
                                      <td className="py-1.5 pl-1 font-mono font-medium">{v?.license_plate || '—'}</td>
                                      <td className="py-1.5 text-gray-600">{v?.make} {v?.model}</td>
                                      <td className="py-1.5 text-center">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catColors[cat]}`}>{cat}</span>
                                      </td>
                                      <td className="py-1.5 text-center">
                                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">{t('insurance.contractModal.toDefine')}</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Claims Tab */}
          {!loading && activeTab === 'claims' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('insurance.contractModal.claimsTitle')}</h3>
                  <p className="text-sm text-gray-500">
                    {t('insurance.contractModal.claimsSummary', { total: claims.length, open: activeClaimsCount })}
                  </p>
                </div>
              </div>

              {claims.length > 0 ? (
                <div className="space-y-3">
                  {claims.map(claim => {
                    const vehicle = getVehicle(claim.vehicle_id);
                    const statusColors = {
                      draft: 'bg-gray-100 text-gray-700',
                      declared: 'bg-blue-100 text-blue-700',
                      acknowledged: 'bg-purple-100 text-purple-700',
                      expert_pending: 'bg-yellow-100 text-yellow-700',
                      under_review: 'bg-orange-100 text-orange-700',
                      approved: 'bg-green-100 text-green-700',
                      denied: 'bg-red-100 text-red-700',
                      settled: 'bg-gray-100 text-gray-600',
                      closed: 'bg-gray-100 text-gray-600',
                    };
                    return (
                      <div
                        key={claim.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                              <span className="font-medium text-gray-900">
                                {t('insurance.contractModal.claimNumber', { number: claim.claim_number || claim.id })}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[claim.status] || statusColors.draft}`}
                              >
                                {t(`insurance.claimStatus.${claim.status}`, { defaultValue: CLAIM_STATUS_LABELS[claim.status] || claim.status })}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {claim.incident_date
                                ? uiFmt.date(claim.incident_date)
                                : t('insurance.contractModal.unknownDate')}
                              {vehicle && ` • ${vehicle.license_plate}`}
                            </p>
                            {claim.description && (
                              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                {claim.description}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {claim.estimated_amount && (
                              <p className="text-lg font-bold text-gray-900">
                                {money(claim.estimated_amount, claim)}
                              </p>
                            )}
                            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-1">
                              {t('insurance.contractModal.viewDetails')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">{t('insurance.contractModal.noClaimTitle')}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('insurance.contractModal.noClaimHint')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {!loading && activeTab === 'documents' && (
            <UnifiedDocumentsTab
              scope="CONTRACT"
              referenceId={contractRefId}
              title={t('insurance.contractModal.documentsTitle')}
              allowedTypes={[
                'FLEET_SCHEDULE',
                'SPECIAL_CONDITIONS',
                'INSURANCE_POLICY',
                'CONTRACT_SCHEDULE',
                'GENERAL_CONDITIONS',
                'CONTRACT_AMENDMENT',
                'INSURANCE_AMENDMENT',
                'CONTRACT_INVOICE',
                'PREMIUM_NOTICE',
                'INSURANCE_CERTIFICATE',
                'INSURANCE_MEMO',
                'INSURANCE_ATTESTATION',
                'INSURANCE_MISC'
              ]}
              acceptedTypeLabels={[
                t('insurance.documentTypes.fleetSchedule'),
                t('insurance.documentTypes.specialConditions'),
                t('insurance.documentTypes.insurancePolicy'),
                t('insurance.documentTypes.guaranteeTable'),
                t('insurance.documentTypes.generalConditions'),
                t('insurance.documentTypes.amendments'),
                t('insurance.documentTypes.premiumNotice'),
                t('insurance.documentTypes.certificates'),
              ]}
              documents={contractDocs}
              loading={docsLoading}
              onRefresh={loadContractDocs}
              onUploadComplete={() => {}}
              onMessage={onMessage}
              onValidate={handleDocumentOpen}
              entityData={contract}
            />
          )}
        </div>

        {/* Vehicle Add Tool */}
        {showAddTool && (
          <VehicleAddTool
            contract={contract}
            onClose={() => setShowAddTool(false)}
            onSuccess={() => {
              // Reload vehicle insurances
              api.vehicleInsurance.getAll({ contract_id: contract.id }).then(res => {
                if (res.ok) setVehicleInsurances((res.data || []).filter(vi => !vi.removed_at));
              });
              onUpdate && onUpdate();
            }}
            onMessage={onMessage}
          />
        )}

        {/* Vehicle Remove Tool */}
        {showRemoveTool && (
          <VehicleRemoveTool
            contract={contract}
            onClose={() => setShowRemoveTool(false)}
            onSuccess={() => {
              // Reload vehicle insurances
              api.vehicleInsurance.getAll({ contract_id: contract.id }).then(res => {
                if (res.ok) setVehicleInsurances((res.data || []).filter(vi => !vi.removed_at));
              });
              onUpdate && onUpdate();
            }}
            onMessage={onMessage}
          />
        )}

        {/* Confirmation Dialog */}
        {confirmingAction && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('insurance.contractModal.confirmActionTitle')}</h3>
              <p className="text-gray-700 mb-6">
                {t('insurance.contractModal.confirmActionQuestion', {
                  action: t(`insurance.workflowActions.${confirmingAction.key}`, { defaultValue: confirmingAction.key }),
                })}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmingAction(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  onClick={confirmAction}
                  className={`flex-1 px-4 py-2 bg-${confirmingAction.color}-500 text-white rounded-lg hover:bg-${confirmingAction.color}-600 font-semibold`}
                >
                  {t('common.actions.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        doc={viewerDoc}
      />

      {/* Document Validation Modal */}
      <DocumentValidationModal
        isOpen={!!validationDoc}
        onClose={() => setValidationDoc(null)}
        doc={validationDoc}
        entityData={contract}
        onValidated={() => {
          loadContractDocs();
          setValidationDoc(null);
          onMessage?.({
            title: t('insurance.contractModal.documentValidatedTitle'),
            message: t('insurance.contractModal.documentValidatedMessage'),
            type: 'success'
          });
        }}
      />

      {/* Insurance Verification Modal */}
      {showVerificationModal && verifyingVi && (
        <InsuranceVerificationModal
          isOpen={showVerificationModal}
          onClose={() => { setShowVerificationModal(false); setVerifyingVi(null); }}
          vehicleId={verifyingVi.vehicle_id}
          vehicleInsuranceId={verifyingVi.id}
          contractId={contract?.id}
          onVerified={() => {
            onMessage?.({ title: t('insurance.messages.successTitle'), message: t('insurance.vehicleTab.verificationSaved'), type: 'success' });
            // Reload verification statuses
            const viList = vehicleInsurances;
            const loadVer = async () => {
              const verMap = {};
              for (const vi of viList) {
                try {
                  const vsRes = await insuranceModuleAPI.getVerificationStatus(vi.vehicle_id);
                  if (vsRes.ok && vsRes.data) verMap[vi.id] = vsRes.data;
                } catch { /* ignore */ }
              }
              setVerificationStatuses(verMap);
            };
            loadVer();
          }}
        />
      )}
    </div>
  );
}

export default ContractDetailModal;
